/**
 * brainPageRegistry — single source of truth for every Brain Intelligence
 * sub-nav page. Declares for each of the 30 pages:
 *
 *   - phase:           OBSERVE | UNDERSTAND | ACT | EVOLVE
 *   - label:           the user-facing name (matches sidebar)
 *   - insight:         "what this page tells you" — 1 sentence, tier:role
 *   - verify:          "how to verify the AI is right" — concrete action
 *   - nextAction:      next-phase deep-link with context contract
 *   - auditSources:    which backend evidence the audit drawer can pull
 *                      (KB doc paths, physics-equation refs, sample
 *                      counts, conformal interval source)
 *   - roleViews:       optional per-role wording (operator / engineer /
 *                      manager / executive) for the SAME insight
 *
 * The TakeawayCard, InvestigationThread, RelatedPagesGrid and
 * InsightAuditDrawer all read from this registry. Adding a new Brain
 * page is a one-line registry entry — no per-page wiring needed.
 *
 * **Why this matters for product coherence:** before this registry,
 * every page's narrative was either hardcoded in component JSX or
 * generated by a per-page util that drifted out of sync. Now there is
 * ONE place a product owner edits to change what users see.
 */

import type { BrainPhase } from '@/store/investigationStore'

export type Role = 'operator' | 'technician' | 'engineer' | 'manager' | 'executive' | 'sme'

export interface AuditSource {
  /** Display label (e.g., "Physics equation: Paris law"). */
  label: string
  /** Endpoint or KB doc path the drawer fetches. */
  ref: string
  /** Optional descriptor of what the drawer should render. */
  kind: 'physics_equation' | 'kb_document' | 'sop' | 'training_sample' |
        'conformal_interval' | 'historical_outcome' | 'data_window' | 'other'
}

export interface NextActionContract {
  label: string
  path: string                                        // brain page path
  /** Static context keys this action contributes to the thread.
   *  Pages compute the values at runtime (rootCause, assetId, etc.). */
  contextKeys: string[]
  /** What the next page does with this context — informational only. */
  consumedBy: string
}

export interface RoleView {
  insight: string
  verify: string
}

export interface BrainPageEntry {
  path: string
  label: string
  phase: BrainPhase
  /** One-sentence default insight (≤140 chars). */
  insight: string
  /** One-sentence default verify (≤140 chars). */
  verify: string
  /** Optional next-action; without one the page is a leaf. */
  nextAction?: NextActionContract
  /** Audit-drawer sources — which evidence the AI cites. */
  auditSources: AuditSource[]
  /** Role-specific overrides; defaults to base insight/verify if absent. */
  roleViews?: Partial<Record<Role, RoleView>>
  /** True if this page is Hub-only (Spoke shows HubOnlyPage). */
  hubOnly?: boolean
  /** Why a user would care — short product-coherence statement. */
  whyItMatters: string
}

// ───────────────────────── OBSERVE (5 pages) ─────────────────────────

export const REGISTRY: BrainPageEntry[] = [
  {
    path: '/brain/cognitive-twin',
    label: 'Live Intelligence',
    phase: 'observe',
    insight:
      'Composite trust score across 17 brain components. Red components are anomalies the brain has self-detected; the fusion score tells you whether the two hemispheres agree.',
    verify:
      'Cross-check the lowest-trust component against the Health Matrix tab — its raw score must match the matrix cell.',
    nextAction: {
      label: 'Trace a low-trust component to its root cause',
      path: '/brain/causal',
      contextKeys: ['source', 'phase'],
      consumedBy: 'Causal Graph seeds the do-calculus query with the affected component',
    },
    auditSources: [
      { label: 'Brain component health', ref: '/api/v2/agi/brain/status', kind: 'data_window' },
      { label: 'Callosum agreement stats', ref: '/api/v2/agi/brain/callosum/stats', kind: 'data_window' },
      { label: 'Calibration report', ref: '/api/v2/cognitive/uncertainty/calibration', kind: 'conformal_interval' },
    ],
    whyItMatters:
      'Operators get a single colour-coded number that tells them whether the AI itself is healthy — before trusting any of its outputs.',
    roleViews: {
      operator: {
        insight: 'Green = AI is healthy. Red = at least one brain part is unsure — slow down before acting on its advice.',
        verify: 'Hover any red dot to see which sensor or model triggered it.',
      },
      executive: {
        insight: 'Aggregate cognitive trust across 17 ML systems. Compares against last-30-day baseline.',
        verify: 'Tab "Trend" shows weekly delta; flat = stable, dropping = trust degrading.',
      },
    },
  },
  {
    path: '/brain/model-health',
    label: 'Model Fitness',
    phase: 'observe',
    insight:
      'Per-asset model health: which models are healthy, degraded, or need retraining. Built from drift signals + conformal coverage + override rate.',
    verify:
      'A degraded model should have either a drift event in the last 7d OR conformal coverage < 90 %. Drill into any row to see which.',
    nextAction: {
      label: 'Trigger auto-recovery for degraded models',
      path: '/brain/healing',
      contextKeys: ['assetId', 'industryId'],
      consumedBy: 'Self-Healing pre-fills the auto-heal queue with the failing models',
    },
    auditSources: [
      { label: 'Per-asset quality reports', ref: '/api/v2/cognitive/quality-report', kind: 'historical_outcome' },
      { label: 'Drift event history', ref: '/api/v2/continual/drift', kind: 'data_window' },
    ],
    whyItMatters:
      'Without this page, you discover model rot by being burned in production. With it, you see decline a week early.',
    roleViews: {
      operator: {
        insight: 'Green rows = trust the alerts. Amber = verify before acting. Red = model is stale, escalate.',
        verify: 'If a model is red, check when the asset last had maintenance — stale data often looks like drift.',
      },
      technician: {
        insight: 'Degraded models mean sensor data has drifted from what the AI was trained on. Likely causes: sensor recalibration, new operating regime, maintenance campaign.',
        verify: 'Drill into any degraded row → "Drift Source" tab shows which sensor shifted most.',
      },
      engineer: {
        insight: 'Rows sorted by drift KS-statistic × conformal gap. Top entries are model-data misalignment; retrain candidates.',
        verify: 'Cross-check the 7-day accuracy trend against override rate — both should fall together if it\'s real drift, not just dataset shift.',
      },
      manager: {
        insight: 'Each degraded asset is a model whose alerts you can no longer trust at face value — humans must review before acting until retraining catches up. The headline count is whatever the live takeaway reports.',
        verify: 'Weekly health delta in the Trend tab — dropping counts mean retraining is keeping up; rising means backlog.',
      },
      executive: {
        insight: 'Portfolio model health indicator. Reducing the degraded count reduces false-alarm rate and missed-failure risk in production.',
        verify: 'Compare this quarter\'s healthy/degraded ratio with last quarter. Trend up → model investment paying off.',
      },
      sme: {
        insight: 'Drift signals + calibration gaps per asset. This page is the first signal that the physics assumptions baked into the model no longer hold for current operations.',
        verify: 'For any degraded row, the drift source should correlate with a physical change (new fuel blend, sensor swap, regime shift) — if not, investigate data quality.',
      },
    },
  },
  {
    path: '/brain/model-quality',
    label: 'Prediction Accuracy',
    phase: 'observe',
    insight:
      'Calibration curves + accuracy trend per asset. Tells you not just IS the model right, but does it KNOW when it is right.',
    verify:
      'A well-calibrated model has predictions clustering on the diagonal; if 90 %-confidence predictions are right only 70 % of the time, the model is over-confident.',
    nextAction: {
      label: 'Compare with feedback queue ground truth',
      path: '/brain/audit',
      contextKeys: ['assetId'],
      consumedBy: 'Prediction Audit shows the per-prediction evidence chain',
    },
    auditSources: [
      { label: 'Calibration report', ref: '/api/v2/cognitive/uncertainty/calibration', kind: 'conformal_interval' },
      { label: 'Accuracy history', ref: '/api/v2/models/accuracy-history', kind: 'data_window' },
      { label: 'Feedback stats', ref: '/api/v2/feedback/stats', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'Calibration is the difference between an AI you can build a maintenance schedule on and a chatbot.',
    roleViews: {
      operator: {
        insight: 'When the AI says it\'s 90% sure, it should be right about 90% of the time. This page shows whether it actually is.',
        verify: 'If the reliability dots sit ON the diagonal line, the AI\'s confidence is honest. Above = paranoid, below = overconfident.',
      },
      technician: {
        insight: 'Per-asset calibration curves. Use to decide whether to trust an AI\'s confidence number or apply a correction factor from field experience.',
        verify: 'A reliability diagram with points well below the diagonal in the high-confidence zone means "when the AI is sure, it\'s often wrong" — treat high-confidence calls with caution.',
      },
      engineer: {
        insight: 'Reliability diagrams + ECE/Brier score per asset. Post-calibration temperature scaling visible. Monitor drift of ECE over time.',
        verify: 'ECE ≤0.05 acceptable; 0.05–0.15 needs recalibration; >0.15 = model is mis-calibrated, investigate before trusting confidence bands.',
      },
      manager: {
        insight: 'AI-confidence quality metric. Well-calibrated AI lets you build maintenance schedules on confidence thresholds.',
        verify: 'Ask: "Can I schedule a shutdown based on the AI saying 95% confidence?" If calibration is good, yes. If poor, no.',
      },
      executive: {
        insight: 'Foundation of trustworthy AI operations. Calibration quality determines whether AI outputs are actionable or advisory.',
        verify: 'Track ECE fleet-average over quarters. Improving = investment paying off; stable = sustained quality; degrading = model-management attention.',
      },
      sme: {
        insight: 'Platt scaling / temperature scaling post-hoc calibration. Per-asset ECE + Brier + reliability diagrams computed on held-out test sets.',
        verify: 'Confirm calibration curves use held-out data, not training data. Training-data calibration is always optimistic — meaningless for production.',
      },
    },
  },
  {
    path: '/brain/fairness',
    label: 'Fairness & Bias',
    phase: 'observe',
    insight:
      'Demographic parity check across asset groups (model, age, location). Surfaces under-served groups before they fail.',
    verify:
      'Fairness score should match the harmonic mean of per-group accuracy; if not, the metric is broken.',
    nextAction: {
      label: 'Drill into the most-biased group in Causal Graph',
      path: '/brain/causal',
      contextKeys: ['assetId', 'industryId'],
      consumedBy: 'Causal Graph re-roots its do-calculus on the affected group',
    },
    auditSources: [
      { label: 'Per-group prediction stats', ref: '/api/v2/brain/fairness/groups', kind: 'data_window' },
    ],
    whyItMatters:
      'Hidden bias means the cheapest assets get the worst predictions, the worst maintenance, and the highest failure rate. This page surfaces it.',
    roleViews: {
      operator: {
        insight: 'Are alerts spread fairly across your assets, or concentrated on a few? Green = fair coverage. Red = some assets are under-monitored.',
        verify: 'Hover any red group — "Group alert rate" should explain what\'s different about those assets. Age, location, or model are common causes.',
      },
      technician: {
        insight: 'Per-group accuracy + alert frequency. Under-served groups get missed failures; over-served groups generate alert fatigue.',
        verify: 'Compare the bias score over time — steady bias suggests training data imbalance; rising bias suggests new population not in training set.',
      },
      engineer: {
        insight: 'Demographic parity + equal-opportunity metrics across asset segments. Harmonic mean of per-group accuracy < population accuracy = bias.',
        verify: 'Breakdown should show: each group\'s support (N), accuracy, false-positive rate, false-negative rate. Large N + low accuracy = priority retraining.',
      },
      manager: {
        insight: 'Fairness across the fleet — are maintenance resources distributed by data-driven need, or is the AI favouring the assets easiest to monitor?',
        verify: 'If the fairness score has been declining, the fleet has drifted; schedule a retraining round with recent data from under-served groups.',
      },
      executive: {
        insight: 'Regulatory-grade fairness monitoring. Prevents worst-outcome scenarios (a whole asset class goes un-alerted) and supports ISO/IEC 42001 AI governance.',
        verify: 'Monthly fairness trend report — flat or improving = good; declining = investigate before audit.',
      },
      sme: {
        insight: 'Group-conditional performance metrics with statistical significance. Use to detect spurious bias (small-N false alarms) vs real bias (consistent under-performance on a well-sampled group).',
        verify: 'Demand per-group confidence intervals. A group flagged "biased" at N<30 is often noise; real bias sticks at N≥100.',
      },
    },
  },
  {
    path: '/brain/video-analysis',
    label: 'Visual Inspection',
    phase: 'observe',
    insight:
      'Camera feed + AR-overlay anomaly detection. Catches physical damage that no sensor sees: leaks, corrosion, foreign objects.',
    verify:
      'Click any flagged frame — the saliency overlay must visibly highlight the anomaly area, not the background.',
    nextAction: {
      label: 'Open the asset card with the visual finding',
      path: '/brain/causal',
      contextKeys: ['assetId', 'anomalyId'],
      consumedBy: 'Causal Graph correlates the visual finding with the sensor evidence chain',
    },
    auditSources: [
      { label: 'Frame analysis result', ref: '/api/v2/ar/video-analysis', kind: 'data_window' },
    ],
    whyItMatters:
      'Visual inspection finds 30 % of failures that sensors miss (vendor data) — and integrates them into the same causal chain.',
    roleViews: {
      operator: {
        insight: 'Upload a photo or pipe a camera feed here — the AI highlights what it sees as damaged or abnormal. Good for catching leaks/corrosion/foreign objects sensors miss.',
        verify: 'The red overlay should sit on the actual damage, not random background. If the overlay is off, don\'t trust the severity estimate.',
      },
      technician: {
        insight: 'Frame-by-frame anomaly detector with AR overlay. Each detection gets a confidence + severity + failure-class. Link to causal graph to check upstream causes.',
        verify: 'Compare the AI\'s anomaly class against physical inspection. Consistent match on 5+ cases = visual model is calibrated for your equipment.',
      },
      engineer: {
        insight: 'Multi-frame temporal attention fusion: per-frame CNN + LSTM over video window. Saliency maps explain which pixels drove each classification.',
        verify: 'Saliency concentrated on damaged region = trusting signal. Scattered saliency = model is reasoning from background texture.',
      },
      manager: {
        insight: 'Visual failure detection layer. Catches failure modes that sensor-based monitoring physically can\'t see.',
        verify: 'Monthly precision/recall on visual detections from field verification logs. Both should stay >70% after model maturation.',
      },
      executive: {
        insight: 'Expands predictive coverage from sensor-only to "whatever you can point a camera at". Typically catches an additional 20–30% of failures vs sensor-only baselines.',
        verify: 'Track dollars saved from visually-detected failures that sensors missed. Categorise as incremental value.',
      },
      sme: {
        insight: 'Vision + temporal attention + class-conditional physics grounding. Anomaly score calibrated against labelled failure-frame dataset.',
        verify: 'Spot-check saliency on both true positives AND false positives — both should tell you something about what the model is attending to. Random saliency on FPs = model guessing.',
      },
    },
  },

  // ───────────────────────── UNDERSTAND (5 pages) ─────────────────────

  {
    path: '/brain/audit',
    label: 'Evidence Trail',
    phase: 'understand',
    insight:
      'Per-prediction proof chain: the inputs, the model snapshot, the SOP rules consulted, and the human override (if any).',
    verify:
      'Every prediction should cite at least one KB doc and one physics equation; if both are blank, the prediction is unmoored.',
    nextAction: {
      label: 'Load this prediction into AI Reasoning',
      path: '/brain/reasoning',
      contextKeys: ['predictionId', 'assetId'],
      consumedBy: 'Reasoning visualizes the chain of inference for this exact prediction',
    },
    auditSources: [
      { label: 'Prediction audit log', ref: '/api/v2/audit/predictions', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'Compliance: every recommendation has a paper trail. Trust: the user can always ask "why did the AI say this?"',
    roleViews: {
      operator: {
        insight: 'For any AI alert you got, click here to see exactly which sensors + rules produced it. If the chain looks thin, trust your gut.',
        verify: 'Every row should cite at least one sensor reading and one reference doc. Blank citations = don\'t act without a second opinion.',
      },
      technician: {
        insight: 'Full drill-down from raw sensor event → feature extraction → model score → rule check → final recommendation. Use this to justify or override.',
        verify: 'Click a prediction → the overrides tab shows how often humans corrected this class of alert. High override rate means the model needs retraining.',
      },
      engineer: {
        insight: 'Per-prediction evidence pack: inputs, model_id, feature_attributions, KB citations, physics checks, final output. All signed with SHA256 for audit.',
        verify: 'SHAP top-3 must attribute >50%. Every cited KB doc must resolve to a version-pinned entry. Missing either = unmoored prediction.',
      },
      manager: {
        insight: 'The paper trail behind every AI decision. Use this to prove due diligence in audits, incident reviews, and compliance reporting.',
        verify: 'Every row filterable by date/asset/status. Export to CSV for compliance packages.',
      },
      executive: {
        insight: 'Auditable AI — every recommendation traceable to sensors + regulatory references. Reduces regulatory risk and improves insurance negotiation posture.',
        verify: 'Sample any month\'s predictions — coverage should be 100% (every decision has a chain). Gaps are a compliance risk.',
      },
      sme: {
        insight: 'Complete reasoning trace per prediction — the only way to critique the AI\'s reasoning at the level a domain expert needs.',
        verify: 'For any prediction you disagree with, the chain should surface at least one feature or rule that, if corrected, would flip the outcome. If not, the model is reasoning from spurious correlations.',
      },
    },
  },
  {
    path: '/brain/reasoning',
    label: 'AI Reasoning',
    phase: 'understand',
    insight:
      'Step-by-step inference visualization for any prediction. Shows which features drove the decision and which were ignored.',
    verify:
      'Top-3 SHAP features must add to >50 % attribution; if not, the model is reasoning from noise.',
    nextAction: {
      label: 'Test the strongest hypothesis in the Hypothesis Lab',
      path: '/brain/hypothesis-lab',
      contextKeys: ['predictionId', 'topFeature'],
      consumedBy: 'Hypothesis Lab pre-loads a what-if on the strongest feature',
    },
    auditSources: [
      { label: 'SHAP attributions', ref: '/api/v2/cognitive/explain/local', kind: 'data_window' },
    ],
    whyItMatters:
      'A black-box prediction is unactionable. A reasoned one tells the SME exactly which lever to pull.',
    roleViews: {
      operator: {
        insight: 'The top bars show which sensors the AI actually looked at. If the biggest bar is a sensor you\'ve been watching, the AI agrees with you.',
        verify: 'Bar direction tells you up or down — red bars pushed the prediction toward a problem; green bars reassured the AI.',
      },
      technician: {
        insight: 'Ranked feature impact per prediction. The top 3 bars tell you what to check first when diagnosing the flagged condition.',
        verify: 'Compare the top feature\'s direction against your maintenance log — if the AI says "high vibration drove this" but recent maintenance lowered vibration, the reasoning is stale.',
      },
      engineer: {
        insight: 'SHAP per-feature contributions with base value and f(x) output. Cross-reference against feature engineering pipeline — unexpected features suggest pipeline bugs.',
        verify: 'Top-3 must attribute >50% of |ΔE[f(x)]|. Sparse reasoning (all features tiny) = either data leak or model memorised noise.',
      },
      manager: {
        insight: 'Prediction transparency — see which measured inputs drove each AI recommendation. Use to brief teams on why the AI is flagging specific issues.',
        verify: 'If the same top-3 features recur across many predictions, those are your leading indicators to watch.',
      },
      executive: {
        insight: 'AI explainability in human terms. Every prediction ranked by contributing factors. Useful for board-level confidence in AI recommendations.',
        verify: 'Request a "reasoning sample" quarterly — pick 5 random predictions, compare their top features to your ops team\'s own hypothesis. Close match = AI and ops agree.',
      },
      sme: {
        insight: 'Local SHAP explanations with expected-value grounding. Each feature\'s contribution measured against the population baseline.',
        verify: 'Stable features (bars similar shape across repeat predictions) indicate robust model reasoning; wildly shifting bars = high variance prediction, treat with caution.',
      },
    },
  },
  {
    path: '/brain/causal',
    label: 'Relationship Map',
    phase: 'understand',
    insight:
      'Each edge is a learned causal link (do-calculus + Granger, NOT just correlation). Thickest red edges are root causes to act on first.',
    verify:
      'Right-click any root-cause node → "Show evidence" to inspect the source events that produced this link.',
    nextAction: {
      label: 'Open the top root cause in Decision Optimizer',
      path: '/brain/decision-optimizer',
      contextKeys: ['rootCause', 'confidence', 'industryId'],
      consumedBy: 'Decision Optimizer ranks corrective actions for this exact root cause',
    },
    auditSources: [
      { label: 'Causal graph (DAG)', ref: '/api/v2/agi/brain/causal/graph', kind: 'data_window' },
      { label: 'Root-cause probabilities', ref: '/api/v2/agi/brain/causal/root-cause', kind: 'data_window' },
    ],
    whyItMatters:
      'Correlation dashboards waste maintenance budget on symptoms. Causal maps point at the actual cure.',
    roleViews: {
      operator: {
        insight: 'The arrow pointing AT an alarm tells you what caused it. Follow the arrow to find what to check first.',
        verify: 'Click any node — the evidence panel shows the historical failures that support this arrow.',
      },
      technician: {
        insight: 'Use this graph to decide where to put the wrench, not just where the alarm is. The upstream node is the true fix; the downstream alarm is the symptom.',
        verify: 'Arrow thickness = number of past failures this link explains. Thin arrows are hypotheses; thick are well-proven.',
      },
      engineer: {
        insight: 'Directed acyclic graph from do-calculus intervention analysis. Edges weighted by causal strength (ATE × data support).',
        verify: 'Compare top-3 root causes against historical failure reports — should overlap ≥80% for mature industries.',
      },
      manager: {
        insight: 'Every intervention has a target — pick the upstream node, save downstream repairs. Usually one root fix prevents 3–5 symptom repairs.',
        verify: 'Look at the "Investigated count" badge — top nodes should already have been addressed in your maintenance history.',
      },
      executive: {
        insight: 'Systemic versus tactical issues. A few high-weight root causes = high ROI on targeted capex; scattered = process problem.',
        verify: 'If the top-3 root causes account for >60% of downstream failures, you have a clear investment target.',
      },
      sme: {
        insight: 'Causal DAG with intervention analysis. Edge strength = effect size of the cause on the outcome, adjusted for confounders.',
        verify: 'Compare the DAG to your own expert mental model — unexpected edges are either data artefacts OR genuine learning.',
      },
    },
  },
  {
    path: '/brain/knowledge',
    label: 'Industry Knowledge',
    phase: 'understand',
    insight:
      'Searchable library of SOPs, regulatory standards, and failure reports the brain consults at every prediction.',
    verify:
      'Search any prediction\'s evidence chain — every cited doc should appear here with the same id.',
    nextAction: {
      label: 'See how rules combine with patterns in the Rule + AI Engine',
      path: '/brain/neuro-symbolic',
      contextKeys: ['industryId'],
      consumedBy: 'Neuro-Symbolic engine shows the rule layer the brain uses on top of patterns',
    },
    auditSources: [
      { label: 'KB documents', ref: '/api/v2/agi/knowledge/stats', kind: 'kb_document' },
      { label: 'KB freshness updates', ref: '/api/v2/agi/knowledge/updates/status', kind: 'data_window' },
    ],
    whyItMatters:
      'A maintained, searchable knowledge base IS the difference between a generic LLM and a domain-aware one.',
    roleViews: {
      operator: {
        insight: 'Search the procedures the AI is using to guide you. If an alert references SOP-45, find it here.',
        verify: 'Every SOP has a "last reviewed" date. Anything >24 months old — flag to your supervisor.',
      },
      technician: {
        insight: 'Your industry\'s failure-mode library + procedure manual, indexed and searchable. This is what the AI cites when it recommends an action.',
        verify: 'Cross-check a cited SOP against your hard-copy. If wording differs, report it — the AI may be working from a stale cache.',
      },
      engineer: {
        insight: 'Structured knowledge base: SOPs, failure-mode datasets, regulatory standards, vendor guidelines. Semantic search + full-text. Freshness and chunking quality tracked per source.',
        verify: 'Knowledge coverage matrix shows which asset types lack docs — add them to improve prediction groundedness.',
      },
      manager: {
        insight: 'Compliance readiness indicator. Every document here is a piece of evidence for ISO/OSHA/API audits. Missing docs = audit risk.',
        verify: 'Reports can export KB contents with citations for regulatory audits.',
      },
      executive: {
        insight: 'Institutional knowledge capital — retained in the AI even when staff turnover happens. Competitive moat.',
        verify: 'Growth in KB doc count per quarter tracks institutional learning investment.',
      },
      sme: {
        insight: 'Every prediction the AI makes can be traced back to sources here. This is your leverage to correct the AI when it drifts from domain truth.',
        verify: 'Flag any cited document that\'s outdated or misinterpreted — the AI will be re-indexed on the next freshness cycle.',
      },
    },
  },
  {
    path: '/brain/knowledge-graph',
    label: 'Entity Knowledge Graph',
    phase: 'understand',
    insight:
      'Equipment ↔ failure ↔ action knowledge graph. Surfaces hidden second-order links between assets.',
    verify:
      'Pick any node → 2-hop neighbours should include only entities that share a real failure mode.',
    nextAction: {
      label: 'Use the strongest neighbourhood for design optimisation',
      path: '/brain/design-studio',
      contextKeys: ['anchorNode'],
      consumedBy: 'Design Studio constrains generative search by the chosen neighbourhood',
    },
    auditSources: [
      { label: 'KG nodes', ref: '/api/v2/knowledge-graph/nodes', kind: 'data_window' },
      { label: 'KG quality metrics', ref: '/api/v2/knowledge-graph/quality-metrics', kind: 'data_window' },
    ],
    whyItMatters:
      'Most failure-mode docs miss the cross-asset links. The KG surfaces them automatically.',
    roleViews: {
      operator: {
        insight: 'This is the parts-and-spec encyclopedia, not the alarm map. Look up an asset and the graph lists every part, every failure mode, and every standard procedure documented for it — then sister-asset matches that share those parts. Use it to find the right document fast, not to chase a live alarm.',
        verify: 'Pick an asset you know inside-out — the linked parts list should match what\'s actually on the bill of materials. Missing or wrong parts = the KB seed was incomplete; ask an SME to add them.',
      },
      technician: {
        insight: 'Sister-asset finder: pick an asset, the graph returns peers that share parts, vendor, vintage, or operating regime. When fixing a rare fault, those peers are where to look for prior repair notes — not where to chase the next failure.',
        verify: 'Run a peer search on a known-quirky asset. The top 3 peers should be ones your team would also flag manually as similar. If peer ranking is dominated by one shared trivial attribute (e.g. "same vendor"), the embedding weights need tuning.',
      },
      engineer: {
        insight: 'Heterogeneous knowledge graph: nodes = {Asset, Part, FailureMode, Procedure, Standard}, edges = typed relations (HAS_PART, AFFECTED_BY, REMEDIED_BY, COMPLIES_WITH). Built from KB docs via LLM extraction + rule-based normalisation. Distinct from the Causal Graph (directed, intervention-grounded) — the KG is undirected/typed and answers "what is related" not "what causes what".',
        verify: 'Node-degree distribution should follow a power law with FailureMode + Procedure as hubs. If Asset is the dominant hub, the extractor mis-classified entities — check the typed-extraction prompt.',
      },
      manager: {
        insight: 'Maintenance-bundling tool: when a planned outage opens, the KG surfaces other assets that share parts/standards with the one being serviced — bundle them into the same window for compounded labour savings. This is a planning aid, not an incident-response aid.',
        verify: 'For your last 3 planned outages, check the KG\'s suggested bundle list against what your planners actually bundled. ≥60% overlap = the graph is reflecting reality. Below that = planners are using context the graph doesn\'t see (e.g. crew-rotation, shutdown sequencing).',
      },
      executive: {
        insight: 'Standardisation lens: which parts/vendors are most-shared across the fleet? High-degree Part nodes are consolidation targets — replacing a one-off variant with the dominant standard cuts spares-inventory cost. The KG quantifies "how many assets would benefit" before you commit.',
        verify: 'Ask for the top-10 highest-degree Part nodes. The procurement savings from standardising those should be visible in next-quarter\'s spares-inventory report. If not, the graph is finding shared parts that are already standardised on paper but not in practice.',
      },
      sme: {
        insight: 'Schema is closer to a property graph than RDF — typed nodes + edge attributes (extraction_confidence, source_doc_id, extracted_at). Edges are LLM-extracted with rule-based post-filter; precision/recall are tracked per relation type and exposed via /api/v2/knowledge-graph/quality-metrics. Embeddings are GraphSAGE-style aggregations usable as features in downstream predictors. Causal direction is intentionally NOT modelled here — that lives in /brain/causal.',
        verify: 'Per-relation precision should be ≥0.85 after the extraction QC pass. Sample 20 edges of each type manually each quarter. If REMEDIED_BY (procedure→failure) drops below 0.8 the LLM extraction prompt is the most likely culprit — failure-mode language is more ambiguous than part numbers.',
      },
    },
  },
  {
    path: '/brain/neuro-symbolic',
    label: 'Rule + AI Engine',
    phase: 'understand',
    insight:
      'Domain rules + neural patterns combined. The rule layer enforces SOPs even when the neural layer wants to do otherwise.',
    verify:
      'Toggle "rules off" — the prediction confidence should DROP, not rise; if it rises, the rules are constraining the wrong cases.',
    nextAction: {
      label: 'Run the rule-augmented prediction in the Decision Optimizer',
      path: '/brain/decision-optimizer',
      contextKeys: ['ruleSet'],
      consumedBy: 'Decision Optimizer uses the rule layer as a feasibility gate',
    },
    auditSources: [
      { label: 'Active rules', ref: '/api/v2/neuro-symbolic/rules', kind: 'sop' },
      { label: 'Ontology', ref: '/api/v2/neuro-symbolic/ontology', kind: 'kb_document' },
    ],
    whyItMatters:
      'Pure ML can\'t guarantee compliance. Pure rules can\'t generalise. Combining them is the only way both work.',
    roleViews: {
      operator: {
        insight: 'The rules protect you from the AI making a decision your SOPs say is illegal. If a rule fires, the AI suggestion is gated.',
        verify: 'When a rule rejects an AI recommendation, you\'ll see a yellow banner. If the banner is wrong (rule should have let it through), report it — the rule may be overfit.',
      },
      technician: {
        insight: 'SOP rules + neural patterns combined. The AI proposes an action; the rule layer approves, modifies, or rejects it based on codified safety and regulatory constraints.',
        verify: 'Toggle "rules only" on any prediction — if accuracy drops, rules are doing real work. If it rises, rules are over-constraining.',
      },
      engineer: {
        insight: 'Hybrid inference: neural logits are consumed by a rules engine (Datalog/CLP-style) that encodes SOPs + regulatory standards. Rule-layer acts as a feasibility gate over the neural layer.',
        verify: 'Rules coverage matrix shows which SOPs are currently active. Gaps = SOPs not yet codified. Full coverage is the compliance ceiling.',
      },
      manager: {
        insight: 'Compliance rails on AI recommendations. Prevents the AI from suggesting anything outside your defined SOPs and standards.',
        verify: 'Active rules list should match your SOP index. Missing rules = AI can suggest out-of-policy actions; add them via the Knowledge Base.',
      },
      executive: {
        insight: 'Hybrid AI+rules: the legally-defensible posture. Every AI recommendation is checked against codified domain rules before it surfaces.',
        verify: 'Audit: sample 20 recent predictions — each should carry a "rules passed" badge showing which SOPs were consulted.',
      },
      sme: {
        insight: 'Neuro-symbolic cascade: neural classification feeds into a deterministic rule engine for constraint-satisfiable action selection. Catches the long-tail cases neural alone gets wrong.',
        verify: 'For any contested prediction, request the trace — should show which rules fired, which facts were consumed, and which ML features were ignored by the rule layer.',
      },
    },
  },

  // ───────────────────────── ACT (7 pages) ────────────────────────────

  {
    path: '/brain/decision-optimizer',
    label: 'Smart Action',
    phase: 'act',
    insight:
      'Options ranked by Bayesian expected utility, with cost / risk / downtime *distributions* not point estimates.',
    verify:
      'Click any option\'s confidence interval — overlapping bars mean the rank is uncertain; that is the case to escalate to a human.',
    nextAction: {
      label: 'Capture the chosen action in Self-Evolution to track outcome',
      path: '/brain/self-evolution',
      contextKeys: ['decisionId', 'industryId'],
      consumedBy: 'Self-Evolution links the action to its measured outcome over time',
    },
    auditSources: [
      { label: 'Optimization run', ref: '/api/v2/decision-optimizer/optimize', kind: 'data_window' },
      { label: 'Cost-risk Monte Carlo', ref: '/api/v2/decision-optimizer/cost-risk', kind: 'conformal_interval' },
      { label: 'Pareto frontier', ref: '/api/v2/decision-optimizer/pareto-frontier', kind: 'data_window' },
    ],
    whyItMatters:
      'CMMS tools recommend "the action". This page recommends the action AND its risk band — the difference between gambling and decision-making.',
    roleViews: {
      operator: {
        insight: 'Every recommendation has a "sure-thing" bar and a "wide-spread" bar. The narrow bars are safe bets; wide bars mean ask a supervisor.',
        verify: 'Hover any option — if the downtime estimate range is wider than 4 hours, escalate before acting.',
      },
      technician: {
        insight: 'Ranked interventions with cost / risk / downtime distributions (not single numbers). Use to pick the option where the worst-case is still acceptable.',
        verify: 'Click "downside" — shows the 90th-percentile outcome. If that outcome is unacceptable, this option is not safe despite a low mean.',
      },
      engineer: {
        insight: 'Bayesian expected utility ranking with Monte Carlo cost-risk distributions. Pareto frontier marks cost-vs-reliability trade-offs.',
        verify: 'Options with overlapping confidence intervals are statistically tied — the top-ranked option is only "best" when bars don\'t overlap.',
      },
      manager: {
        insight: 'Decision support with quantified risk. Every recommendation is accompanied by "what could go wrong" at defined confidence levels.',
        verify: 'Compare the top option\'s downtime distribution to your crew\'s weekly capacity. If 90th-percentile downtime exceeds capacity, schedule carefully.',
      },
      executive: {
        insight: 'Risk-aware capital allocation. The difference between "fix the asset" and "fix the asset, knowing there\'s a 15% chance it takes 2× longer than expected".',
        verify: 'Compare recommended vs chosen actions over a quarter — tracking the AI\'s recommendation accuracy builds trust for bigger decisions.',
      },
      sme: {
        insight: 'Decision optimization with full uncertainty propagation — every ranking is reproducible from the underlying Monte Carlo sample and cost model.',
        verify: 'Request the simulation seed + cost model assumptions — the ranking should be reproducible within ±2% with the same inputs.',
      },
    },
  },
  {
    path: '/brain/hypothesis-lab',
    label: 'What-If Scenarios',
    phase: 'act',
    insight:
      'Generate and stress-test improvement hypotheses against the digital twin BEFORE committing in the field.',
    verify:
      'Each hypothesis should improve at least one KPI without degrading any safety bound; if any safety bound red-lines, reject.',
    nextAction: {
      label: 'Promote the best hypothesis to AI Experiments',
      path: '/brain/experiment-lab',
      contextKeys: ['hypothesisId'],
      consumedBy: 'Experiment Lab schedules a longitudinal trial for the chosen hypothesis',
    },
    auditSources: [
      { label: 'Generated hypotheses', ref: '/api/v2/experiments/hypotheses', kind: 'data_window' },
    ],
    whyItMatters:
      'Operators stop guessing — they test in simulation first. Failure cost drops by an order of magnitude.',
    roleViews: {
      operator: {
        insight: 'Before committing to a change on the real equipment, the AI can try it in a simulator first. This page lists candidate changes and their simulated outcomes.',
        verify: 'A green hypothesis improves one KPI without red-lining any safety bound. Red hypotheses should never leave simulation.',
      },
      technician: {
        insight: 'Ranked improvement ideas with simulated outcomes. Use this to pick maintenance adjustments worth trying vs those that look good but could harm throughput.',
        verify: 'Each hypothesis shows its simulated KPI delta. Compare to your field experience — if a hypothesis claims +20% uptime and contradicts known physics, reject.',
      },
      engineer: {
        insight: 'Hypothesis generation via perturbation of operating variables, stress-tested against the physics-grounded digital twin. Safety bounds enforced before any candidate can ship.',
        verify: 'Each hypothesis has a pre-registered success criterion. Post-sim evaluation should match criterion — if not, the sim is mis-calibrated or the criterion was wrong.',
      },
      manager: {
        insight: 'Low-cost experimentation ground. Test 10 ideas for the cost of committing to one — the best ideas get promoted to real trials.',
        verify: 'Aggregate "hypothesis to trial" rate. Healthy programs promote 20–40% of generated hypotheses. Higher = underselling; lower = overfitting to the sim.',
      },
      executive: {
        insight: 'Innovation pipeline de-risked by simulation. Reduces cost of experimentation by an order of magnitude vs field-only A/B testing.',
        verify: 'Track annualized KPI uplift from hypothesis-originated interventions. Should be a measurable contributor to operational KPIs within 6 months.',
      },
      sme: {
        insight: 'Generate → simulate → gate → promote hypothesis loop. Each candidate stress-tested against the twin with Monte Carlo uncertainty propagation before any field commitment.',
        verify: 'Sim-to-field delta should be within ±20% for promoted hypotheses. Larger deltas = twin not capturing a key physics element; halt promotion until twin is recalibrated.',
      },
    },
  },
  {
    path: '/brain/experiment-lab',
    label: 'AI Experiments',
    phase: 'act',
    insight:
      '6 AI specialists (statistician, physicist, economist, …) co-design experiments. Real DoE, not vibes.',
    verify:
      'Statistical power > 80 % AND a pre-registered success criterion; if either is missing, the experiment is theatre.',
    nextAction: {
      label: 'Promote successful experiments to Self-Evolution',
      path: '/brain/self-evolution',
      contextKeys: ['experimentId'],
      consumedBy: 'Self-Evolution schedules the rollout & monitors the outcome',
    },
    auditSources: [
      { label: 'Experiment plan', ref: '/api/v2/experiments', kind: 'data_window' },
      { label: 'Specialist proposals', ref: '/api/v2/agentic/experiments/proposals', kind: 'data_window' },
    ],
    whyItMatters:
      'Most "AI improvements" never get tested rigorously. This page is the rigour: pre-registration, power, gates.',
    roleViews: {
      operator: {
        insight: 'Pending trials on your shift. Each trial has a simple "what to watch" line — usually a single KPI.',
        verify: 'If an experiment says "run for 48h" but shift schedule says otherwise, coordinate with the lead before starting.',
      },
      technician: {
        insight: 'Ongoing and completed experiments. Green badge means the intervention helped; red means rolled back. Use results to justify SOP changes.',
        verify: 'Every completed trial has a "result vs expectation" delta. Large negative deltas are learning opportunities — share in post-mortems.',
      },
      engineer: {
        insight: 'DoE with power ≥0.8 and pre-registered success criteria. Multi-specialist design: statistician (sample size), physicist (feasibility), economist (cost justification).',
        verify: 'Red "theatre" flag triggers when power <0.8 or no pre-registration. Fix both before running — otherwise the trial is unfalsifiable.',
      },
      manager: {
        insight: 'AI-accelerated improvement program. Each trial is a structured attempt to improve a KPI — measurable, with statistical rigor.',
        verify: 'Quarterly trial count + success rate tracks continuous-improvement pace. 5–10 trials/quarter with 30–60% success rate is typical.',
      },
      executive: {
        insight: 'Structured innovation pipeline with measurable ROI per experiment. Replaces "gut feel" with "we ran a trial and it moved KPI X by Y%".',
        verify: 'Aggregate KPI lift × asset volume = annualized improvement from experimentation. Board-reportable.',
      },
      sme: {
        insight: 'Rigorous experimental design with domain-expert specialist agents. Every trial has a pre-registered hypothesis, power analysis, and success gate.',
        verify: 'Audit any "successful" trial — the p-value, effect size, and confidence interval must all point the same direction. Disagreement = invalid inference.',
      },
    },
  },
  {
    path: '/brain/twin',
    label: 'Behavior Simulator',
    phase: 'act',
    insight:
      'Physics + AI digital twin. Simulate any equipment under any condition — without touching the real asset.',
    verify:
      'Twin output should match historical sensor data on the same input within sensor noise; if not, the twin is mis-calibrated.',
    nextAction: {
      label: 'Validate twin output against the Physics Validator',
      path: '/brain/physics',
      contextKeys: ['assetId', 'scenarioId'],
      consumedBy: 'Physics Lab checks first-principles consistency of the twin\'s trajectory',
    },
    auditSources: [
      { label: 'Twin trajectory', ref: '/api/v2/features/simulation/run', kind: 'data_window' },
      { label: 'Physics models', ref: '/api/v2/features/simulation/physics/models', kind: 'physics_equation' },
    ],
    whyItMatters:
      'Test failure modes you never want to test in production. The Twin is the safest gym in the plant.',
    roleViews: {
      operator: {
        insight: 'Like a flight simulator for your equipment — try any scenario here before touching the real asset.',
        verify: 'Compare a historical event you lived through — the twin should reproduce it within sensor noise. If not, the twin is out of calibration.',
      },
      technician: {
        insight: 'Run scenarios safely: what if temperature spikes 10°C? What if bearing load doubles? The twin tells you what will happen.',
        verify: 'Twin replay of a known failure should land in the same failure mode within ±10% on time-to-failure.',
      },
      engineer: {
        insight: 'Physics + ML hybrid digital twin. First-principles ODE/PDE for known dynamics + ML for learned behaviors. Runs in Gym-like step() interface for RL.',
        verify: 'Validate against historical trajectories: MAPE <10% on key KPIs, coverage ≥90% inside physical bounds.',
      },
      manager: {
        insight: 'A safe place to test what-if scenarios. Saves capital by avoiding failed experiments on real equipment.',
        verify: 'Track simulator-predicted vs real outcome on every deployed scenario. >80% agreement = twin earns its maintenance budget.',
      },
      executive: {
        insight: 'Operational "wind tunnel" — simulate expensive failure modes for free, quantify intervention ROI before committing capital.',
        verify: 'Sim-predicted ROI should land within ±20% of realised ROI for promoted interventions. Track quarterly.',
      },
      sme: {
        insight: 'Multi-modal digital twin: physics + causal + learned components. Explicit uncertainty on each component\'s contribution.',
        verify: 'Twin should expose per-timestep confidence. Wide bands in novel regions = extrapolation; narrow bands = near-training-distribution.',
      },
    },
  },
  {
    path: '/brain/physics',
    label: 'Physics Validator',
    phase: 'act',
    insight:
      'First-principles equations check every ML prediction. Catches when the AI contradicts thermodynamics or kinematics.',
    verify:
      'Any flagged constraint violation should reproduce when you re-run with the original inputs; if not, the validator drifted.',
    nextAction: {
      label: 'Open a what-if on the violated equation in the Twin',
      path: '/brain/twin',
      contextKeys: ['equationId'],
      consumedBy: 'Twin simulates the asset under the constraint to find the safe envelope',
    },
    auditSources: [
      { label: 'Curated equations', ref: '/api/v2/context/physics/{industry_id}', kind: 'physics_equation' },
      { label: 'SOP regulatory bounds', ref: '/api/v2/features/simulation/physics/insights', kind: 'sop' },
    ],
    whyItMatters:
      'An ML model can suggest a chiller COP of 8 (impossible). The Physics Validator stops that from reaching the user.',
    roleViews: {
      operator: {
        insight: 'If the AI suggests something that violates physics — too-hot, too-cold, impossible pressure — this page flags it before you act.',
        verify: 'Every flagged violation names a specific equation. Click it to see the safe range; your reading should be inside.',
      },
      technician: {
        insight: 'Real-time physics check on every AI suggestion. Violations are auto-blocked from reaching the recommendation queue.',
        verify: 'Re-run a previously flagged violation with the same inputs — it should reproduce. Drift here means the validator itself needs recalibration.',
      },
      engineer: {
        insight: 'Thermodynamic / kinematic / conservation-law constraint checker running ODE-based first-principles against ML outputs. Violations tagged by equation id.',
        verify: 'Validator false-positive rate should be < 2% against historical operating data; false-negatives should be 0 for known physical impossibilities.',
      },
      manager: {
        insight: 'Safety guardrail: the AI cannot recommend physically impossible set-points. Every suggestion is physics-checked before a human sees it.',
        verify: 'Weekly: count of blocked violations × estimated incident cost if unblocked. This is the dollar value of the guardrail.',
      },
      executive: {
        insight: 'Hard-constraint layer between ML and operations. Regulators see it as the difference between "AI-assisted" and "AI-governed" operations.',
        verify: 'Audit readiness: every prediction has a physics-validation attestation. Missing attestations are non-compliant.',
      },
      sme: {
        insight: 'First-principles validator with curated per-industry equation library. Each equation is traceable to a textbook citation and a regulatory bound where applicable.',
        verify: 'Any new equation added to the library should include provenance (text + page), and at least 3 historical test cases it correctly flags.',
      },
    },
  },
  {
    path: '/brain/design-studio',
    label: 'Design Optimizer',
    phase: 'act',
    insight:
      'Generative AI proposes optimised operating configurations under your constraints. Pareto-front, not single answer.',
    verify:
      'Each design should beat the current baseline on at least 2 KPIs; if a design wins on one and loses on others, surface that trade-off.',
    nextAction: {
      label: 'Promote the chosen design to a longitudinal experiment',
      path: '/brain/experiment-lab',
      contextKeys: ['designId'],
      consumedBy: 'Experiment Lab schedules a controlled rollout of the design',
    },
    auditSources: [
      { label: 'Design candidates', ref: '/api/v2/features/simulation/generate-designs', kind: 'data_window' },
    ],
    whyItMatters:
      'Stop hand-tuning operating set-points. Generative search finds Pareto-better configs in minutes, not weeks.',
    roleViews: {
      operator: {
        insight: 'The AI suggests better operating recipes — safer, more efficient, tested in simulation. You still decide whether to try them.',
        verify: 'Every design shows predicted vs current on 2+ metrics. If the trade-off isn\'t explicit, ask for it before accepting.',
      },
      technician: {
        insight: 'Generative search over operating configurations. Each candidate is physics-validated and twin-tested before it appears on the Pareto front.',
        verify: 'Click a candidate to see its simulated trajectory. The set-point trajectory should stay inside the physics-validator safe envelope at all times.',
      },
      engineer: {
        insight: 'Multi-objective Bayesian optimization with generative proposals (VAE latent search + evolutionary refinement). Constraints enforced via physics + SOP guards.',
        verify: 'Check Pareto-front diversity: at least 5 non-dominated candidates across the objective space. Clustering = local optimum; explore more.',
      },
      manager: {
        insight: 'Automated continuous improvement on operating parameters. Delivers 2–8% efficiency gain per quarter on average when recommendations are adopted.',
        verify: 'Track adoption rate: % of recommended designs that advance to Experiment Lab. Healthy adoption is 20–40% (more = under-filtering; less = team not trusting the AI).',
      },
      executive: {
        insight: 'Continuous-improvement engine grounded in simulation. Replaces ad-hoc tweaking with auditable, Pareto-optimal design search.',
        verify: 'Quarterly: realized KPI uplift on adopted designs ÷ predicted uplift. Ratio ≥ 0.8 means the twin predicts reality well enough to trust.',
      },
      sme: {
        insight: 'Generative design with multi-objective Pareto front + uncertainty quantification. Each candidate has its own posterior over predicted KPIs.',
        verify: 'Low-uncertainty candidates should cluster near existing operating envelope; high-uncertainty ones are exploration. Promote only low-uncertainty candidates to field trial.',
      },
    },
  },
  {
    path: '/brain/chat',
    label: 'Ask The AI',
    phase: 'act',
    insight:
      'Conversational query against the cognitive engine grounded in physics + KB SOPs + per-asset model.',
    verify:
      'Every assistant message cites a KB doc id AND a physics equation; if both are missing, the answer is hallucinated.',
    nextAction: {
      label: 'Pin the chat answer into Decision Optimizer',
      path: '/brain/decision-optimizer',
      contextKeys: ['conversationId'],
      consumedBy: 'Decision Optimizer uses the chat insight as a candidate option',
    },
    auditSources: [
      { label: 'AI response', ref: '/api/v2/oracle/ask', kind: 'data_window' },
      { label: 'Cited KB docs', ref: '/api/v2/agi/knowledge/query', kind: 'kb_document' },
    ],
    whyItMatters:
      'A general-purpose LLM hallucinates. The Oracle cites — every answer is auditable.',
    roleViews: {
      operator: {
        insight: 'Ask questions in plain English. The AI answers from your equipment data + procedures, not from the general internet. Every answer cites its sources.',
        verify: 'If an answer has no citations at the bottom, don\'t trust it — the AI may be guessing.',
      },
      technician: {
        insight: 'Natural-language query over the cognitive engine. Answers ground in physics equations + KB docs + per-asset model state. Verbose mode shows reasoning steps.',
        verify: 'A good answer cites ≥1 KB doc AND ≥1 equation. Cite-free answers are not production-safe to act on.',
      },
      engineer: {
        insight: 'LLM inference with retrieval-augmented generation over industry KB + physics equations + current model state. Response includes citations + confidence + alternative interpretations.',
        verify: 'Check RAG citations are real (doc exists in KB) and relevant (retrieval score >0.7). Fabricated citations = hallucination, escalate.',
      },
      manager: {
        insight: 'Team-accessible AI assistant grounded in your plant\'s specific data and procedures. Reduces "go ask the engineer" round-trips for routine queries.',
        verify: 'Random-audit 10 team queries per month. Correct answer + valid citations = AI is helping; hallucinations = team needs refresher on how to use it.',
      },
      executive: {
        insight: 'Domain-grounded AI assistant — replaces tribal-knowledge bottlenecks by making institutional knowledge instantly queryable.',
        verify: 'Track team adoption (queries/person/week) + answer acceptance rate. High adoption + high acceptance = AI is a productivity force.',
      },
      sme: {
        insight: 'Multi-stage inference: intent classification → RAG retrieval → physics grounding → LLM answer synthesis → confidence calibration. Every stage traceable.',
        verify: 'Demand the full inference trace on any contested answer. Mis-retrievals or hallucinations are detectable in the trace; answer-only views hide the failure mode.',
      },
    },
  },

  // ───────────────────────── EVOLVE (13 pages) ────────────────────────

  {
    path: '/brain/self-evolution',
    label: 'Auto-Improvement',
    phase: 'evolve',
    insight:
      'How the AI adapts: drift events, augmentations, retrains, measured uplift. The brain\'s public report card.',
    verify:
      'Cycles labelled "improvement" should have a measured KPI uplift > 0; if not, label them "no-op", not improvement.',
    nextAction: {
      label: 'Inspect the latest cycle\'s memory writes',
      path: '/brain/memory',
      contextKeys: ['cycleId'],
      consumedBy: 'Memory Browser shows what the brain learned from this cycle',
    },
    auditSources: [
      { label: 'Evolution status', ref: '/api/v2/self-evolution/status', kind: 'data_window' },
      { label: 'Cycle history', ref: '/api/v2/self-evolution/history', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'A static model rots. This page proves the brain is improving — measurably, week over week.',
    roleViews: {
      operator: {
        insight: 'Green "improvement" rows = the AI just got better at your equipment. Red "no-op" = the cycle ran but didn\'t learn anything new, data was quiet.',
        verify: 'If there\'s a green improvement row from last week, alerts on that equipment should be slightly more reliable this week.',
      },
      technician: {
        insight: 'Each cycle shows what changed in the model + whether it helped. Use this to know whether to trust the AI\'s latest recommendations vs last week\'s.',
        verify: 'Click any cycle → shows the "before" and "after" metrics side by side. Uplift > 0 means the change stuck.',
      },
      engineer: {
        insight: 'Drift → augment → retrain → validate → deploy cycle log. Every entry has measured uplift on held-out validation; negative uplift triggers automatic rollback.',
        verify: 'Rollback events should be <5% of cycles. Higher rollback rate = the drift-augment-retrain loop is destabilising, reduce sensitivity.',
      },
      manager: {
        insight: 'Weekly AI improvement rate. Steady green cycles = AI is learning from production; frequent no-ops = system stable; rollbacks = investigate.',
        verify: 'A good month shows 2–4 improvement cycles, ≤1 no-op, 0 rollbacks. Deviations are trend signals.',
      },
      executive: {
        insight: 'Measurable AI improvement ledger. Each uplift entry is an auditable productivity gain — useful for justifying AI investment ROI.',
        verify: 'Summed uplifts × prediction volume = quantified improvement over the period. Track quarter-over-quarter.',
      },
      sme: {
        insight: 'Self-evolution log: drift detection → synthetic data augmentation → model retrain → holdout validation → auto-deploy (or auto-rollback). Each cycle is reproducible from its evidence pack.',
        verify: 'Reproducibility check — pull any cycle\'s evidence pack, rerun on the cached data, confirm the uplift claim matches.',
      },
    },
  },
  {
    path: '/brain/healing',
    label: 'Auto-Recovery',
    phase: 'evolve',
    insight:
      'When AI fixes itself: what broke, what got repaired, what got better. The self-healing log.',
    verify:
      'Every healing event should reference the model snapshot before AND after; if either is missing, the audit chain is broken.',
    nextAction: {
      label: 'Trace a healing event back to its triggering anomaly',
      path: '/brain/audit',
      contextKeys: ['healingEventId'],
      consumedBy: 'Audit shows the prediction → anomaly → healing chain',
    },
    auditSources: [
      { label: 'Healing history', ref: '/api/v2/continual/healing', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'When the model self-heals, MTTR drops to minutes. This page is the receipt.',
    roleViews: {
      operator: {
        insight: 'When the AI catches itself slipping, this page shows what it fixed and whether it worked. Green row = fixed; red row = needs human eyes.',
        verify: 'After a healing event, open the affected equipment page — its model trust should be back above amber within ~30 min.',
      },
      technician: {
        insight: 'Every auto-recovery event: what triggered it (anomaly/drift), what the AI changed (rolled back / retrained / recalibrated), whether it worked. Your first stop when an asset starts misbehaving.',
        verify: 'Each event should cite both the pre-heal snapshot and the post-heal snapshot. Missing either = incomplete audit trail.',
      },
      engineer: {
        insight: 'Auto-recovery log with before/after diffs. Covers drift-triggered retrains, rollback on regression, calibration refits. Each event reproducible from its evidence pack.',
        verify: 'Healing success rate should be ≥80%. Below that, the trigger/repair heuristics need tuning.',
      },
      manager: {
        insight: 'MTTR-reducing mechanism. Every successful auto-heal saves a human intervention cycle.',
        verify: 'Compare MTTR since auto-recovery was enabled vs before. A meaningful drop (>30%) justifies the compute cost.',
      },
      executive: {
        insight: 'Operational resilience indicator — AI self-repairs faster than humans can diagnose. Directly impacts uptime SLA delivery.',
        verify: 'Annualized hours-saved = events × mean-human-response-time. Translate to $ via downtime cost per hour.',
      },
      sme: {
        insight: 'Self-healing ledger with full audit chain: anomaly signal → healing action → validation → deploy or rollback. Every action is falsifiable from the stored evidence.',
        verify: 'Audit a healing event: the pre-heal model should fail the test that the post-heal model passes. If both pass, the healing was unnecessary.',
      },
    },
  },
  {
    path: '/brain/memory',
    label: 'Learning Memory',
    phase: 'evolve',
    hubOnly: true,
    insight:
      'Episodic memory + EWC Fisher matrix. What the brain remembers — ensures no critical pattern is forgotten on retrain.',
    verify:
      'Fisher importance for a known-critical feature should be in the top 10 %; if it sinks below, retrain priorities are wrong.',
    nextAction: {
      label: 'Inspect the dreaming queue (replays of past anomalies)',
      path: '/brain/dreaming',
      contextKeys: [],
      consumedBy: 'Dreaming Monitor shows which past patterns the brain re-rehearses',
    },
    auditSources: [
      { label: 'Memory events', ref: '/api/v2/cognitive/memory', kind: 'data_window' },
    ],
    whyItMatters:
      'Catastrophic forgetting is the silent killer of online ML. EWC + episodic memory is the only proven defence.',
    roleViews: {
      operator: {
        insight: 'The AI remembers rare events it has seen before — so even if a failure mode only happens once a year, the AI won\'t forget how to recognize it.',
        verify: 'Search for a past incident you remember. If the AI can recall its signature (without being retrained), memory is working.',
      },
      technician: {
        insight: 'Searchable log of rare/important events the AI has seen. Use to confirm the AI has "memory" of a specific failure mode before trusting it on that pattern.',
        verify: 'Critical events (explosions, trip avoidances, near-misses) should show up with high importance scores. If importance is low, they may be lost on the next retrain.',
      },
      engineer: {
        insight: 'EWC Fisher importance matrix + episodic replay buffer. Fisher protects critical weights during gradient updates; replay buffer re-exposes rare examples.',
        verify: 'Known-critical feature Fisher values should be in the top decile. If they sink, the model will over-write important behaviors on the next training run.',
      },
      manager: {
        insight: 'Institutional memory preservation — ensures the AI never forgets critical incidents as it learns new patterns.',
        verify: 'Count of preserved "critical events". Should only grow over time. Decreases mean memory is being evicted — check retention policy.',
      },
      executive: {
        insight: 'Regulatory/compliance indispensable — AI must retain knowledge of near-miss events and safety-critical modes even as it adapts.',
        verify: 'Request a "memory audit" quarterly — random sample of stored incidents to verify they\'re still retrievable and still attributed.',
      },
      sme: {
        insight: 'Continual-learning memory substrate: EWC regularization on the parameter space + experience replay for data-side preservation. Fisher matrix computed per-task.',
        verify: 'Fisher diagonals should correlate with task importance. A corner of low-Fisher on a known-important parameter = EWC failure, escalate.',
      },
    },
  },
  {
    path: '/brain/dreaming',
    label: 'Scenario Generator',
    phase: 'evolve',
    insight:
      'AI imagines failure modes you have never seen. Generates synthetic anomalies + tests how the brain would respond.',
    verify:
      'Each generated scenario should include both the input pattern AND the expected ground-truth label; if missing, the brain is dreaming alone.',
    nextAction: {
      label: 'Run the strongest dream-scenario in the Twin',
      path: '/brain/twin',
      contextKeys: ['dreamId'],
      consumedBy: 'Twin runs the dream scenario through the physics + ML stack',
    },
    auditSources: [
      { label: 'Dreaming history', ref: '/api/v2/agi/brain/dreaming/history', kind: 'data_window' },
    ],
    whyItMatters:
      'Black-swan failures are unforeseen by definition. Dreaming is how the brain prepares for them.',
    roleViews: {
      operator: {
        insight: 'The AI runs imagined failure scenarios overnight. If one of these imagined scenarios matches something you later see for real, the AI is already prepared.',
        verify: 'Scan imagined scenarios for any that look like operating conditions you\'re worried about — if they\'re missing, tell your engineer so the AI can practice them.',
      },
      technician: {
        insight: 'Generated failure scenarios the AI tests itself on. Use this as a pre-emptive failure-mode coverage check — what failure modes does the AI know how to handle?',
        verify: 'Each imagined scenario should include a label + expected AI response. Missing labels = the AI is imagining without knowing the right answer.',
      },
      engineer: {
        insight: 'Generative synthetic data (VAE/GAN-based) for rare/unseen failure modes. Each dream scenario stress-tests the brain\'s classification and response policy.',
        verify: 'Dream coverage should span the known failure-mode taxonomy. Gaps indicate missing generation priors; flag the taxonomy entry.',
      },
      manager: {
        insight: 'Proactive failure-mode preparation. The AI invents hard cases nightly and trains on them — cheaper than real-world failure.',
        verify: 'Count of unique failure modes rehearsed per week. Healthy: increasing with diminishing returns. Flat count = dreaming loop stuck.',
      },
      executive: {
        insight: 'Resilience investment — AI prepares for scenarios your ops team has never documented. Reduces black-swan risk.',
        verify: 'When a rare event happens in production, trace whether the AI had dreamed a similar one. Dreaming value = precision on novel events.',
      },
      sme: {
        insight: 'Generative replay with counterfactual augmentation — VAE for in-distribution variations, GAN for boundary-pushing novelty. All dreams are labelled by the physics + rules engine before replay.',
        verify: 'Dream scenarios should include both the synthetic input AND the physics-based ground truth. Dreams without ground truth are hallucinations, not training signal.',
      },
    },
  },
  {
    path: '/brain/security',
    label: 'Robustness Shield',
    phase: 'evolve',
    insight:
      'Adversarial probes: can the AI be fooled? How. Continuous red-team output.',
    verify:
      'Every successful attack should have a remediation linked within 24 h; if not, the shield is paper-thin.',
    nextAction: {
      label: 'Add the failing case to the experiment queue',
      path: '/brain/experiment-lab',
      contextKeys: ['probeId'],
      consumedBy: 'Experiment Lab proves the remediation works under power',
    },
    auditSources: [
      { label: 'Red-team status', ref: '/api/v2/agi/brain/red-team/status', kind: 'data_window' },
      { label: 'Probe history', ref: '/api/v2/agi/brain/red-team/history', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'AI systems get attacked. Knowing how — before the attacker does — is what makes life-critical AI safe.',
    roleViews: {
      operator: {
        insight: 'The AI tries to trick itself with fake-bad inputs, then counts how many tricks work. Low success rate (red bars short) = the AI is tough to fool.',
        verify: 'Every probe that succeeds should have a "fix queued" status. If fixes aren\'t queued, report to your supervisor.',
      },
      technician: {
        insight: 'Adversarial test log. Shows how the AI responds to distorted, noisy, or out-of-distribution inputs. Failed probes are weak spots needing a patch.',
        verify: 'A successful probe should reveal which input feature or perturbation flipped the AI. Use this to add defensive training data.',
      },
      engineer: {
        insight: 'Red-team probe library: FGSM/PGD/boundary attacks + OOD + label-poison + backdoor. Continuous evaluation against production model snapshots.',
        verify: 'Probe success rate should decline over time as remediations merge. Flat or rising = remediations aren\'t closing the gap.',
      },
      manager: {
        insight: 'AI robustness monitoring — the continuous stress-test. Safety-critical AI fails silently without this.',
        verify: 'Track weekly probe success rate. Trend line dropping = hardening is working. Flat = stalemate, plateau in defenses.',
      },
      executive: {
        insight: 'Proactive risk management for AI. Finds vulnerabilities before bad actors or unexpected operating conditions do.',
        verify: 'Tie probe findings to incident-response SLAs. Every successful probe should have a 24h-to-remediation commitment.',
      },
      sme: {
        insight: 'Structured adversarial evaluation covering gradient-based, decision-boundary, input-perturbation, and data-poisoning attack families. Per-probe severity + reproducibility.',
        verify: 'Every successful attack must be reproducible with the stored random seed + model hash. Non-reproducible probes = noise, not finding.',
      },
    },
  },
  {
    path: '/brain/metacognition',
    label: 'Confidence Gate',
    phase: 'evolve',
    insight:
      'When the AI defers to humans. Tracks every "I don\'t know" event and what happened next.',
    verify:
      'A high deferral rate on a stable dataset means the gate is paranoid; on a shifting dataset, it means honest. Check drift first.',
    nextAction: {
      label: 'Investigate the deferral spike in Self-Evolution',
      path: '/brain/self-evolution',
      contextKeys: ['deferralReason'],
      consumedBy: 'Self-Evolution checks whether a retrain cycle followed the spike',
    },
    auditSources: [
      { label: 'MetaCognition log', ref: '/api/v2/agi/brain/metacognition/log', kind: 'historical_outcome' },
      { label: 'Decision traces', ref: '/api/v2/agi/brain/metacognition/traces', kind: 'data_window' },
    ],
    whyItMatters:
      'AI that knows what it does NOT know is the safest AI. This page measures that meta-knowledge.',
    roleViews: {
      operator: {
        insight: 'When the AI isn\'t sure, it asks for help instead of guessing. Every "I don\'t know" is logged here so a human can step in.',
        verify: 'If the AI defers on a case you know the answer to, feed back the right answer — the AI learns from your correction.',
      },
      technician: {
        insight: 'Defer-to-human log. Every uncertain prediction gets flagged for human review rather than silently guessed.',
        verify: 'Deferral rate should match case difficulty. Flat 5% on simple cases + rising on edge cases = well-calibrated; random = broken.',
      },
      engineer: {
        insight: 'Confidence-gate metacognition layer. Deferral triggered when prediction confidence < threshold OR when conformal coverage suggests distributional novelty.',
        verify: 'Plot deferral rate vs class balance over time. Sudden spikes without class-balance change = drift or model degradation.',
      },
      manager: {
        insight: 'Quality control layer — ensures the AI escalates to humans when it should, rather than silently degrading.',
        verify: 'Track time-to-resolution on deferred cases. Long queues = AI is deferring too much OR humans are overwhelmed.',
      },
      executive: {
        insight: 'Safety posture indicator — AI knows its own limits. Reduces liability on life-critical decisions.',
        verify: 'Compare deferred-case outcomes vs auto-decided-case outcomes. Deferrals should have materially better outcomes on hard cases.',
      },
      sme: {
        insight: 'Meta-cognitive calibration gate — uses ensemble disagreement, feature-space distance, and conformal intervals to flag uncertain predictions.',
        verify: 'Deferral should strongly correlate with true error rate. If deferral rate < error rate, the gate is under-calibrated; swap to a stricter threshold.',
      },
    },
  },
  {
    path: '/brain/goals',
    label: 'Objective Alignment',
    phase: 'evolve',
    insight:
      'Aligns AI actions with business OKRs (safety > uptime > efficiency > cost). Every recommendation tied to a measurable objective.',
    verify:
      'Every recommendation must declare which OKR it advances; if "general improvement", the alignment is broken.',
    nextAction: {
      label: 'Review the OKR dashboard',
      path: '/brain/self-evolution',
      contextKeys: ['okrId'],
      consumedBy: 'Self-Evolution measures which OKRs the recent cycles advanced',
    },
    auditSources: [
      { label: 'Active OKRs', ref: '/api/v2/brain/goals/okrs', kind: 'data_window' },
    ],
    whyItMatters:
      'AI without business goals optimises noise. Linking every action to an OKR makes the AI accountable.',
    roleViews: {
      operator: {
        insight: 'Every AI recommendation should say "this helps uptime" or "this reduces cost". If you can\'t see which goal an AI suggestion supports, ignore it.',
        verify: 'Look at the badge on each recommendation. "General improvement" = un-aligned and untrustworthy; specific goal = actionable.',
      },
      technician: {
        insight: 'Recommendations ranked by OKR advancement: safety > uptime > efficiency > cost. Use the ordering to pick which to tackle first.',
        verify: 'If two recommendations claim the same OKR, the one with higher confidence interval wins. Close-confidence ties = escalate to supervisor.',
      },
      engineer: {
        insight: 'OKR-alignment layer: each AI output tagged with the specific objective it advances + expected delta on that metric. Weighted MCTS over OKR tree.',
        verify: 'Sample recommendations against their claimed OKR delta. Predicted delta should match realised delta within ±20% on post-hoc review.',
      },
      manager: {
        insight: 'Makes AI accountable to your strategy. Every recommendation cites which business goal it advances, not just "predicted anomaly".',
        verify: 'Quarterly: check which OKRs the AI most often advances vs your strategic priorities. Misalignment = retune the OKR weights.',
      },
      executive: {
        insight: 'Strategic alignment of AI — ensures technical recommendations map to measurable business objectives, not local optimisations.',
        verify: 'Each board period: AI-recommended actions → realised OKR movement. If the correlation is weak, the OKR tree needs refinement.',
      },
      sme: {
        insight: 'OKR hierarchy is encoded as a weighted utility tree (lexicographic safety > uptime > efficiency > cost) and the orchestrator emits each insight with `insight_type` ∈ {critical, warning, optimization, improvement} mapped to the leaf objective. Ranking score = importance × confidence with a 0.7 cutoff for "high-importance" promotion to Smart Actions. An untyped or "general" insight breaks the alignment chain and won\'t be considered for the next planning round.',
        verify: 'The decision trace exposes the per-insight `(insight_type, importance, confidence, related_variables)` tuple. If `aligned_pct` (in the takeaway metrics) trends below 70% for a week, the orchestrator\'s tagging logic has regressed — usually a missing rule in `agi_orchestrator.classify_insight`. Cross-check against the smart-action queue: every promoted action should cite back to one of these insight rows.',
      },
    },
  },
  {
    path: '/brain/federated-learning',
    label: 'Multi-Site Learning',
    phase: 'evolve',
    insight:
      'Learn from other sites without sharing sensitive data. Federated weights + transfer-matrix + differential privacy.',
    verify:
      'Privacy budget (epsilon) should be < 1.0 over the contract window; if not, sites are leaking more than they think.',
    nextAction: {
      label: 'Compare federated vs local model accuracy',
      path: '/brain/model-quality',
      contextKeys: ['federatedRoundId'],
      consumedBy: 'Model Quality compares the federated update with local-only baseline',
    },
    auditSources: [
      { label: 'Federated status', ref: '/api/v2/federated/status', kind: 'data_window' },
      { label: 'Privacy budget', ref: '/api/v2/federated/privacy-budget', kind: 'conformal_interval' },
    ],
    whyItMatters:
      'Multi-site learning beats local 90 % of the time without ever moving raw data — the only viable path for regulated industries.',
    roleViews: {
      operator: {
        insight: 'Your site\'s AI learns from sister sites without anyone sharing raw data. A small privacy number (< 1.0) means the exchange stays confidential.',
        verify: 'The round list shows which sites contributed and when. If your site is absent from recent rounds, its model won\'t get the latest learnings.',
      },
      technician: {
        insight: 'Cross-site federated averaging with differential-privacy noise injection. Local data never leaves; only encrypted weight deltas travel.',
        verify: 'After each round, compare local-only vs federated loss curve. Federated should match or beat local; if worse, the transfer matrix is misaligned.',
      },
      engineer: {
        insight: 'FedAvg with DP-SGD gradient clipping + Gaussian noise, ε-budget tracked per round. Transfer matrix weighted by site data-similarity.',
        verify: 'Privacy budget (ε) should stay < 1.0 across the contract window. Cumulative ε > 1 = privacy guarantee weakened; pause rounds and review noise calibration.',
      },
      manager: {
        insight: 'Participate in multi-site learning without exposing your data. Required for regulated sectors where raw-data sharing is legally impossible.',
        verify: 'Compliance report: every round shows ε budget spent + legal attestation of no raw-data egress. Any round missing either is non-compliant.',
      },
      executive: {
        insight: 'Privacy-preserving fleet learning — strategic moat for multi-site operators. Each site gets smarter without violating data-residency constraints.',
        verify: 'Quarterly review: federated model accuracy uplift vs local-only baseline × number of participating sites = the network effect value.',
      },
      sme: {
        insight: 'Secure aggregation with differential privacy, per-site data-similarity weighting, and adversarial-robust aggregation rules (trimmed-mean, Krum).',
        verify: 'Inspect per-round aggregator choice: FedAvg when sites are iid, trimmed-mean when heterogeneous. Wrong aggregator for the data distribution = model degradation.',
      },
    },
  },
  {
    path: '/brain/agent-teams',
    label: 'Autonomous Agents',
    phase: 'evolve',
    hubOnly: true,
    insight:
      'Multi-agent coordination + autonomous approvals. Five roles (Observer → Planner → Executor → Evaluator → Evolutor).',
    verify:
      'Every approved action must show consensus from at least 3 of 5 roles; if not, the coordination is single-point-of-failure.',
    nextAction: {
      label: 'Review last week\'s agent decisions',
      path: '/brain/audit',
      contextKeys: ['agentRunId'],
      consumedBy: 'Audit shows the per-decision evidence chain',
    },
    auditSources: [
      { label: 'Agent run log', ref: '/api/v2/agents/runs', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'Single-agent systems have single-point biases. Multi-agent consensus is how production-grade autonomy works.',
    roleViews: {
      operator: {
        insight: 'Five AI specialists check each other before any automated action. Nothing ships without at least three of them agreeing.',
        verify: 'The run log shows which roles approved and which objected. Any action approved by fewer than 3 roles is a consensus failure — investigate.',
      },
      technician: {
        insight: 'Multi-agent coordination: Observer gathers evidence, Planner proposes, Executor schedules, Evaluator validates, Evolutor learns from outcomes.',
        verify: 'Every run has a full 5-role trace. Missing roles (e.g., no Evaluator) means the action bypassed a safety gate.',
      },
      engineer: {
        insight: 'Decentralized role-based agent architecture with Byzantine-resilient consensus (3-of-5 approval required). Evolutor closes the learning loop via outcome-weighted role weights.',
        verify: 'Role-disagreement rate should stabilize at 10–30% (full agreement = group-think; full disagreement = coordination failure). Each divergence is a learning signal.',
      },
      manager: {
        insight: 'Safety-first autonomous operations: AI can act, but only with internal multi-party approval. Audit trail for every automated decision.',
        verify: 'Monthly: % of autonomous actions with clean 5-role approval trace. Target > 95%. Sub-95% = consensus protocol is breaking down.',
      },
      executive: {
        insight: 'Production-grade autonomy with built-in checks and balances. Treat it like a committee decision, not a single-AI call — that\'s what makes it defensible.',
        verify: 'Board-level: ratio of autonomous interventions to human overrides. Stable ratio = trust holding; drifting up = reduce autonomy level, drifting down = increase.',
      },
      sme: {
        insight: 'Multi-role agent architecture with specialized expertise (Observer sees, Planner reasons, Evaluator validates), Byzantine-fault-tolerant consensus, and outcome-based role reweighting.',
        verify: 'Audit role contributions: each role\'s decision influence should correlate with its domain expertise on the problem. Off-domain dominance = mis-routed question.',
      },
    },
  },
  {
    path: '/brain/meta-orchestra',
    label: 'Weekly AI Upgrade',
    phase: 'evolve',
    insight:
      'System-wide self-improvement: what got better this week. MAML outer-loop on top of EffortRouter.',
    verify:
      'Each weekly delta must come with a holdout-validated improvement metric; if reported only on training data, it is theatre.',
    nextAction: {
      label: 'Inspect the improved decision policy',
      path: '/brain/decision-optimizer',
      contextKeys: ['policyVersion'],
      consumedBy: 'Decision Optimizer A/Bs the new policy against the previous',
    },
    auditSources: [
      { label: 'Meta-Orchestra status', ref: '/api/v2/self-evolution/meta-orchestra/status', kind: 'data_window' },
      { label: 'Policy traces', ref: '/api/v2/self-evolution/meta-orchestra/traces', kind: 'historical_outcome' },
    ],
    whyItMatters:
      'A weekly upgrade rhythm — measured, audited, reversible — beats irregular "we tweaked it" announcements.',
    roleViews: {
      operator: {
        insight: 'Every week the AI publishes what it learned and how much better it is. You can roll back any upgrade if it feels wrong in the field.',
        verify: 'Each weekly entry lists a specific metric improvement. "Feels better" without a number = marketing, not a real upgrade.',
      },
      technician: {
        insight: 'MAML outer-loop updates decision-routing weights based on last week\'s outcomes. Every upgrade is held-out validated and versioned.',
        verify: 'Compare pre/post holdout metrics for each weekly rollout. A rollout without a positive holdout delta should auto-revert; if not, the gate is broken.',
      },
      engineer: {
        insight: 'Model-agnostic meta-learning (MAML) + bandit-based EffortRouter weight updates. Weekly cadence with auto-rollback on regression.',
        verify: 'Weekly delta p-value should be < 0.05 on the primary KPI. Promotions without statistical significance are noise; tighten the gate.',
      },
      manager: {
        insight: 'Measurable weekly AI improvement with full audit trail. Each upgrade comes with KPI delta, rollback plan, and holdout validation.',
        verify: 'Track realised KPI uplift vs promoted uplift. Delta > 20% in either direction means the holdout is not representative; re-partition the holdout set.',
      },
      executive: {
        insight: 'Systematized continuous improvement — weekly, measured, reversible. Strategic advantage compared to "we retrain when we remember to".',
        verify: 'Quarterly: cumulative KPI gain × asset fleet size = annualized improvement run-rate from the meta-orchestra. Board-reportable number.',
      },
      sme: {
        insight: 'Meta-learning outer loop over the bandit-based EffortRouter inner loop. Uses last-week\'s reward signal to update per-task effort-allocation priors.',
        verify: 'Inspect the MAML gradient norms per week — if they collapse to zero, the meta-loss has found a local optimum and learning has stalled. Add fresh task distributions.',
      },
    },
  },
  {
    path: '/brain/artifacts',
    label: 'Team Workspace',
    phase: 'evolve',
    insight:
      'Capture expert SME knowledge during team investigations. Real-time collaborative artifact editor.',
    verify:
      'Every artifact should reference the investigation thread it captured; if floating, it cannot be re-traced.',
    nextAction: {
      label: 'Promote the artifact to industry KB',
      path: '/brain/knowledge',
      contextKeys: ['artifactId'],
      consumedBy: 'Knowledge ingests the artifact as a new KB doc',
    },
    auditSources: [
      { label: 'Artifact list', ref: '/api/v2/collab/artifacts', kind: 'data_window' },
    ],
    whyItMatters:
      'Tribal knowledge dies when senior SMEs leave. Capturing it in artifacts converts memory into institutional asset.',
    roleViews: {
      operator: {
        insight: 'Shared notebook for the team. Write up what you found, attach evidence, and the next shift inherits the context instead of starting fresh.',
        verify: 'Tag every artifact with the assets it relates to, so teammates can find it by searching their equipment.',
      },
      technician: {
        insight: 'Collaborative investigation workspace. Pin predictions, link causal traces, document findings — everything searchable across teams.',
        verify: 'An artifact that doesn\'t link to at least one audit trail or prediction is a floating note. Add citations so others can retrace your reasoning.',
      },
      engineer: {
        insight: 'OT-style collaborative document editor with real-time multi-user editing + link-out to every evidence source. Artifacts are versioned.',
        verify: 'Every artifact should have a linked evidence pack and a clear "why this matters" lead paragraph. Missing either = incomplete capture.',
      },
      manager: {
        insight: 'Institutional memory system. Preserves expert knowledge before SME rotations or departures.',
        verify: 'Track artifacts created per month per team. Healthy knowledge capture = 3–10 per team per month. Zero = team isn\'t using the workspace.',
      },
      executive: {
        insight: 'Moat-building knowledge preservation. Tribal knowledge becomes institutional asset, surviving staff turnover.',
        verify: 'Biannual: sample artifacts older than 12 months. Those still referenced = knowledge still valuable; orphans = KB noise.',
      },
      sme: {
        insight: 'Collaborative-document workspace with full evidence linking, version history, and role-based edit permissions. Each artifact promotable to the KB.',
        verify: 'Promoted artifacts should have go through peer review first. If promotions are direct, KB quality degrades with each unchecked addition.',
      },
    },
  },
  {
    path: '/brain/industry-discovery',
    label: 'New Industry Detector',
    phase: 'evolve',
    hubOnly: true,
    insight:
      'Auto-detect new data domains from unknown sources — bootstraps a new industry profile from one CSV.',
    verify:
      'Discovered industries should have a confidence > 0.6 for at least one signature feature; if all features are weak, do not auto-create.',
    nextAction: {
      label: 'Review the proposed industry profile',
      path: '/brain/knowledge',
      contextKeys: ['industryId'],
      consumedBy: 'Knowledge inspects the auto-generated industry SOPs / KPIs / equations',
    },
    auditSources: [
      { label: 'Discovery proposals', ref: '/api/v2/data/industries/proposals', kind: 'data_window' },
    ],
    whyItMatters:
      'Onboarding a new vertical used to take weeks. The Discovery Engine collapses it to one CSV upload.',
    roleViews: {
      operator: {
        insight: 'Upload any CSV and the AI tells you what kind of assets it sees. No schema setup, no config — just the data.',
        verify: 'The proposal shows detected columns and their inferred meaning. Check a few rows by hand; if the inference is wrong, flag it before approving.',
      },
      technician: {
        insight: 'Automatic domain detection via column-signature matching + semantic role inference. Unknowns over 0.6 confidence are promoted to draft industry profiles.',
        verify: 'Each proposal lists its signature-match score. Accept only those with > 0.6 on at least one core feature; weaker matches should go back for more data.',
      },
      engineer: {
        insight: 'MultiModal schema inference (numeric / categorical / temporal / text) + sentence-transformer column-name embedding matched against industry prior library.',
        verify: 'For accepted proposals, run the generated profile on a held-out portion of the CSV — prediction coverage should be ≥ 80%. Lower = profile is under-specified.',
      },
      manager: {
        insight: 'Drops customer onboarding time from weeks to minutes. New verticals come online with one CSV instead of multi-week scoping engagements.',
        verify: 'Track: hours-from-first-CSV-to-first-prediction. Target < 2 hours. Above 4 hours = auto-discovery is failing and human setup is filling the gap.',
      },
      executive: {
        insight: 'Horizontal expansion capability — any new vertical self-bootstraps. Strategic cost driver: marginal new-industry onboarding approaches zero.',
        verify: 'Quarterly: count of industries onboarded via auto-discovery ÷ total. Ratio should trend up as more priors accumulate.',
      },
      sme: {
        insight: 'Domain-agnostic ingestion with semantic column inference, signature-based industry matching, and generic-physics-profile fallback for novel domains.',
        verify: 'For a novel-domain proposal (no signature match), the GenericPhysicsProfile fallback should engage and produce feasible bounds. If bounds are infeasible, the fallback needs tuning.',
      },
    },
  },
]

// ───────────────────────── Lookup helpers ───────────────────────────────

const BY_PATH: Record<string, BrainPageEntry> = Object.fromEntries(
  REGISTRY.map((e) => [e.path, e])
)

export function getRegistryEntry(path: string): BrainPageEntry | undefined {
  return BY_PATH[path]
}

export function getEntriesForPhase(phase: BrainPhase): BrainPageEntry[] {
  return REGISTRY.filter((e) => e.phase === phase)
}

export function resolveRoleView(
  entry: BrainPageEntry,
  role?: Role
): { insight: string; verify: string } {
  if (role && entry.roleViews?.[role]) {
    const r = entry.roleViews[role]!
    return { insight: r.insight, verify: r.verify }
  }
  return { insight: entry.insight, verify: entry.verify }
}
